import type { WaScope } from './types';

/**
 * Every query key, in one place. Keys are hierarchical so one invalidation
 * covers every variant beneath it: `keys.tenants.all` matches the staff list,
 * the tenant's own list and every members list. Pages never spell a key; they
 * call the hooks in api/<domain>.ts, and mutations invalidate through these.
 */

/** The cache segment for a WhatsApp scope: staff pass the tenant, a tenant user passes nothing. */
export function scopeKey(scope: WaScope = {}): string {
  return scope.tenantId ?? 'self';
}

export const keys = {
  admin: {
    profile: (tenantId?: string) => ['admin', 'profile', tenantId ?? null] as const,
  },
  tenants: {
    all: ['tenants'] as const,
    /** GET /admin/auth/tenants (staff). */
    adminList: (query: object) => ['tenants', 'admin', query] as const,
    /** GET /tenants. */
    list: (query: object) => ['tenants', 'list', query] as const,
    /** GET /tenants/:tenantId. */
    detail: (tenantId: string | null) => ['tenants', tenantId, 'detail'] as const,
    /** GET /tenants/:tenantId/overview; every tenant-scoped write invalidates the tenants key. */
    overview: (tenantId: string | null) => ['tenants', tenantId, 'overview'] as const,
    members: (tenantId: string | null) => ['tenants', tenantId, 'members'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (query: object) => ['users', 'list', query] as const,
  },
  whitelist: {
    all: ['whitelist'] as const,
    list: (query: object) => ['whitelist', 'list', query] as const,
    check: (email: string | null) => ['whitelist', 'check', email] as const,
  },
  email: {
    all: ['email'] as const,
    /** The tenant to domain mapping the hub still stores. */
    domains: (tenantId: string | null) => ['email', 'domains', tenantId] as const,
    /** GET /tenants/:id/email/check — the live provider answer, never cached across mounts. */
    check: (tenantId: string | null, domain: string | null) =>
      ['email', 'check', tenantId, domain] as const,
    /** GET /email/check?domain= — the Email lab's direct check, no tenant. */
    toolsCheck: (domain: string | null) => ['email', 'tools-check', domain] as const,
    /** GET /email/credentials — on demand, from the Email lab. */
    credentials: ['email', 'credentials'] as const,
  },
  notifications: {
    all: ['notifications'] as const,
    list: (query: object) => ['notifications', 'list', query] as const,
  },
  plans: {
    all: ['plans'] as const,
    solutions: ['plans', 'solutions'] as const,
    list: (query: object) => ['plans', 'list', query] as const,
    subscriptions: (tenantId: string | null) => ['plans', 'subscriptions', tenantId] as const,
    features: (tenantId: string | null) => ['plans', 'features', tenantId] as const,
  },
  whatsapp: {
    all: ['whatsapp'] as const,
    /** Staff's list of every connected account. */
    accounts: ['whatsapp', 'accounts'] as const,
    /** Everything cached for one scope; every write in that scope invalidates it. */
    scope: (scope?: WaScope) => ['whatsapp', scopeKey(scope)] as const,
    config: (scope?: WaScope) => ['whatsapp', scopeKey(scope), 'config'] as const,
    contacts: (scope: WaScope | undefined, query: object) =>
      ['whatsapp', scopeKey(scope), 'contacts', query] as const,
    contact: (scope: WaScope | undefined, id: string | null) =>
      ['whatsapp', scopeKey(scope), 'contacts', id] as const,
    consentTrail: (scope: WaScope | undefined, id: string | null) =>
      ['whatsapp', scopeKey(scope), 'contacts', id, 'consent'] as const,
    templates: (scope: WaScope | undefined, query: object) =>
      ['whatsapp', scopeKey(scope), 'templates', query] as const,
    campaigns: (scope: WaScope | undefined, query: object) =>
      ['whatsapp', scopeKey(scope), 'campaigns', query] as const,
    campaign: (scope: WaScope | undefined, id: string | null) =>
      ['whatsapp', scopeKey(scope), 'campaigns', id] as const,
    analytics: (scope: WaScope | undefined, query: object) =>
      ['whatsapp', scopeKey(scope), 'analytics', query] as const,
    usage: (scope: WaScope | undefined, query: object) =>
      ['whatsapp', scopeKey(scope), 'usage', query] as const,
    events: (scope: WaScope | undefined, query: object) =>
      ['whatsapp', scopeKey(scope), 'events', query] as const,
  },
  audit: {
    all: ['audit'] as const,
    /** GET /audit/events. */
    list: (query: object) => ['audit', 'list', query] as const,
  },
  status: {
    all: ['status'] as const,
    /** GET /status; POST /status/check writes its fresh report here. */
    current: ['status', 'current'] as const,
  },
  cloudflare: {
    all: ['cloudflare'] as const,
    /** GET /cloudflare/overview; POST /cloudflare/refresh writes its fresh answer here. */
    overview: ['cloudflare', 'overview'] as const,
    /** Every zone's DNS record list; a refresh marks them all stale. */
    zones: ['cloudflare', 'zones'] as const,
    dnsRecords: (zoneId: string | null) => ['cloudflare', 'zones', zoneId, 'dns-records'] as const,
  },
  onboarding: {
    all: ['onboarding'] as const,
    /** GET /tenants/:id/onboarding; a PATCH on one item writes the whole answer back here. */
    tenant: (tenantId: string | null) => ['onboarding', tenantId] as const,
  },
};
